import {
  Component,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
  HostListener,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OfferedRewardRule } from '../../models/rewards-v2.models';
import { OfferedRewardCardComponent } from '../offered-reward-card/offered-reward-card.component';

@Component({
  selector: 'app-offered-rewards-carousel',
  standalone: true,
  imports: [CommonModule, OfferedRewardCardComponent],
  templateUrl: './offered-rewards-carousel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./offered-rewards-carousel.component.scss'],
})
export class OfferedRewardsCarouselComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  @Input({ required: true }) rules: OfferedRewardRule[] = [];
  @ViewChild('viewport') viewportRef?: ElementRef<HTMLElement>;

  currentPage = 0;
  cardsPerPage = 2;
  pages: OfferedRewardRule[][] = [];
  isDragging = false;
  transitionEnabled = true;
  dragOffsetPx = 0;

  private dragStartX = 0;
  private viewportWidth = 0;
  private slideStepPx = 0;
  private readonly mobileSlidePeekPx = 16;
  private readonly onTouchMove = (event: TouchEvent): void => {
    this.onPointerMove(event);
    if (this.isDragging) {
      event.preventDefault();
    }
  };
  private boundMouseMove?: (event: MouseEvent) => void;
  private boundMouseUp?: (event: MouseEvent) => void;

  ngOnInit(): void {
    this.rebuildPages();
  }

  ngAfterViewInit(): void {
    this.viewportRef?.nativeElement.addEventListener('touchmove', this.onTouchMove, {
      passive: false,
    });
    this.updateSlideMetrics();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rules']) {
      this.rebuildPages();
    }
  }

  ngOnDestroy(): void {
    this.viewportRef?.nativeElement.removeEventListener('touchmove', this.onTouchMove);
    this.unbindMouseListeners();
  }

  @HostListener('window:resize')
  onResize(): void {
    const previousCardsPerPage = this.cardsPerPage;
    this.updateCardsPerPage();
    if (previousCardsPerPage !== this.cardsPerPage) {
      this.rebuildPages();
    }
    this.updateSlideMetrics();
  }

  get showIndicators(): boolean {
    return this.pages.length > 1;
  }

  get trackTransform(): string {
    if (this.usesMobileSlideStep) {
      const base = this.currentPage * this.slideStepPx;
      if (this.dragOffsetPx === 0) {
        return `translateX(-${base}px)`;
      }
      return `translateX(calc(-${base}px + ${this.dragOffsetPx}px))`;
    }

    const offset = this.currentPage * 100;
    if (this.dragOffsetPx === 0) {
      return `translateX(-${offset}%)`;
    }
    return `translateX(calc(-${offset}% + ${this.dragOffsetPx}px))`;
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.pages.length || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
  }

  onPointerDown(event: TouchEvent | MouseEvent): void {
    if (this.pages.length <= 1) {
      return;
    }

    if (event instanceof MouseEvent && event.button !== 0) {
      return;
    }

    this.isDragging = true;
    this.transitionEnabled = false;
    this.dragStartX = this.getClientX(event);
    this.updateSlideMetrics();

    if (event instanceof MouseEvent) {
      event.preventDefault();
      this.bindMouseListeners();
    }
  }

  onPointerMove(event: TouchEvent | MouseEvent): void {
    if (!this.isDragging) {
      return;
    }

    let delta = this.getClientX(event) - this.dragStartX;

    if (this.currentPage === 0 && delta > 0) {
      delta *= 0.35;
    } else if (this.currentPage === this.pages.length - 1 && delta < 0) {
      delta *= 0.35;
    }

    this.dragOffsetPx = delta;
  }

  onPointerUp(): void {
    if (!this.isDragging) {
      return;
    }

    this.isDragging = false;
    this.transitionEnabled = true;
    this.unbindMouseListeners();

    const threshold = Math.min(this.viewportWidth * 0.2, 80);

    if (this.dragOffsetPx < -threshold && this.currentPage < this.pages.length - 1) {
      this.currentPage += 1;
    } else if (this.dragOffsetPx > threshold && this.currentPage > 0) {
      this.currentPage -= 1;
    }

    this.dragOffsetPx = 0;
  }

  trackByRuleId(_: number, rule: OfferedRewardRule): string {
    return rule.id;
  }

  trackByPageIndex(index: number): number {
    return index;
  }

  private get usesMobileSlideStep(): boolean {
    return this.cardsPerPage === 1 && this.slideStepPx > 0;
  }

  private rebuildPages(): void {
    this.updateCardsPerPage();
    this.pages = this.chunkRules(this.rules, this.cardsPerPage);
    if (this.currentPage >= this.pages.length) {
      this.currentPage = Math.max(this.pages.length - 1, 0);
    }
    this.updateSlideMetrics();
  }

  private updateSlideMetrics(): void {
    const viewport = this.viewportRef?.nativeElement.offsetWidth ?? 0;
    this.viewportWidth = viewport;

    if (this.cardsPerPage === 1 && viewport > 0) {
      this.slideStepPx = viewport - this.mobileSlidePeekPx;
      return;
    }

    this.slideStepPx = viewport;
  }

  private updateCardsPerPage(): void {
    this.cardsPerPage = typeof window !== 'undefined' && window.innerWidth <= 768 ? 1 : 2;
  }

  private chunkRules(rules: OfferedRewardRule[], size: number): OfferedRewardRule[][] {
    if (!rules.length || size < 1) {
      return [];
    }
    const pages: OfferedRewardRule[][] = [];
    for (let i = 0; i < rules.length; i += size) {
      pages.push(rules.slice(i, i + size));
    }
    return pages;
  }

  private getClientX(event: TouchEvent | MouseEvent): number {
    if ('changedTouches' in event) {
      return event.touches[0]?.clientX ?? event.changedTouches[0]?.clientX ?? 0;
    }
    return event.clientX;
  }

  private bindMouseListeners(): void {
    this.boundMouseMove = (event: MouseEvent) => this.onPointerMove(event);
    this.boundMouseUp = () => this.onPointerUp();
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  private unbindMouseListeners(): void {
    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove);
      this.boundMouseMove = undefined;
    }
    if (this.boundMouseUp) {
      document.removeEventListener('mouseup', this.boundMouseUp);
      this.boundMouseUp = undefined;
    }
  }
}
